import { Injectable, NgZone, OnDestroy, inject } from '@angular/core';
import { Subscription, fromEvent, merge } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { CrmAuthSessionService } from './crm-auth-session.service';
// By file, not through the `libs/core` barrel — see the note in app.ts.
import { AuthService } from '../../../../../../../libs/core/auth/auth.service';

/** Shared across every tab of this origin, so activity anywhere counts everywhere. */
const LAST_ACTIVITY_KEY = 'crm.last-activity-at';

/**
 * Detects user inactivity (idle time).
 * After a configurable timeout with no user interaction, triggers logout.
 *
 * Activity is tracked per-tab but published to localStorage, because logout is
 * *not* per-tab: it POSTs the refresh token to /auth/logout, which revokes it
 * server-side and ends the session in every open tab. Advisers routinely keep a
 * case and a client record open side by side, so a tab-local timer meant the
 * untouched tab logged the user out of the one they were working in.
 */
@Injectable({ providedIn: 'root' })
export class CrmIdleTimeoutService implements OnDestroy {
  /**
   * Idle timeout in milliseconds (default: 15 minutes).
   */
  private readonly idleTimeoutMs = 15 * 60 * 1000;
  private readonly checkIntervalMs = 10_000; // re-check every 10s

  /** Mousemove fires constantly; the shared timestamp only needs to be roughly
   *  current, so writes are throttled well below the 10s check interval. */
  private readonly activityPublishIntervalMs = 5_000;

  private readonly authService = inject(AuthService);
  private readonly sessionService = inject(CrmAuthSessionService);
  private readonly ngZone = inject(NgZone);

  private activitySub: Subscription | null = null;
  private intervalId: ReturnType<typeof setInterval> | null = null;
  private lastActivity = Date.now();
  private lastPublishedActivity = 0;
  private isRunning = false;

  /** The DOM events that reset the idle timer. */
  private readonly activityEvents = [
    'mousedown',
    'mousemove',
    'keydown',
    'scroll',
    'touchstart',
    'click',
  ];

  start(): void {
    if (this.isRunning || typeof window === 'undefined') return;
    this.isRunning = true;
    this.markActive();

    // Registered outside the Angular zone, which matters more than it looks.
    //
    // zone.js patches addEventListener, so a listener created in the zone makes
    // its event schedule a change-detection pass. `mousemove` and `scroll` fire
    // hundreds of times a second, and the CRM runs default change detection over
    // 300-odd components, so in-zone listeners here meant a full-tree check on
    // every pixel of cursor movement — app-wide input lag with no visible cause.
    //
    // debounceTime() does not help: it debounces *this* callback, not the zone
    // tick, and its own timer is a zone task too. Only leaving the zone does.
    //
    // Nothing in this path touches the UI — markActive() writes a field and a
    // localStorage key — so there is nothing for a tick to render. Real clicks
    // and keystrokes still trigger change detection through the (click)/(input)
    // bindings Angular registers on the components themselves; those are
    // untouched. The one branch that does affect the UI (logout on timeout)
    // re-enters the zone explicitly in checkIdle().
    //
    // distinctUntilChanged() used to sit in this pipe and did nothing — every
    // DOM event is a new object, so no two are ever "the same" by reference.
    this.ngZone.runOutsideAngular(() => {
      const streams = this.activityEvents.map((eventName) =>
        fromEvent(document, eventName, { passive: true }),
      );

      this.activitySub = merge(...streams)
        .pipe(debounceTime(300))
        .subscribe(() => {
          this.markActive();
        });

      // Periodic idle check
      this.intervalId = setInterval(() => this.checkIdle(), this.checkIntervalMs);
    });
  }

  /** Records activity for this tab and, at most every few seconds, for the others. */
  private markActive(): void {
    const now = Date.now();
    this.lastActivity = now;

    if (now - this.lastPublishedActivity < this.activityPublishIntervalMs) {
      return;
    }

    this.lastPublishedActivity = now;

    try {
      localStorage.setItem(LAST_ACTIVITY_KEY, String(now));
    } catch {
      // Private mode / blocked storage: fall back to tab-local idle tracking.
    }
  }

  /** The most recent activity seen by any tab, this one included. */
  private readSharedActivity(): number {
    try {
      const raw = localStorage.getItem(LAST_ACTIVITY_KEY);
      const parsed = raw ? Number(raw) : NaN;
      // A timestamp from the future (clock change, tampering) must not be able
      // to hold a session open indefinitely.
      return Number.isFinite(parsed) && parsed <= Date.now() ? parsed : 0;
    } catch {
      return 0;
    }
  }

  stop(): void {
    this.activitySub?.unsubscribe();
    this.activitySub = null;

    if (this.intervalId !== null) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }

    this.isRunning = false;
  }

  ngOnDestroy(): void {
    this.stop();
  }

  private checkIdle(): void {
    if (!this.authService.isAuthenticated()) return;

    // The session is shared, so the timeout is measured against the last activity
    // in *any* tab — not just this one.
    const lastActivity = Math.max(this.lastActivity, this.readSharedActivity());
    const idleMs = Date.now() - lastActivity;

    if (idleMs >= this.idleTimeoutMs) {
      // Deliberately not stop()ing. logout() navigates to /login without
      // unmounting the app, so tearing the timer down here left the idle guard
      // dead for the rest of the page's life — a user who signed back in
      // afterwards was never timed out again. checkIdle() is a no-op while
      // signed out, and logout() de-dupes concurrent calls.
      this.markActive();
      // Back into the zone: start() runs this timer outside it, and logout()
      // shows a toast and navigates, both of which need change detection to
      // run afterwards.
      this.ngZone.run(() => {
        this.sessionService.logout('You have been logged out due to inactivity.');
      });
    }
  }
}